import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, Plus, Edit3, Trash2, Search, Save, X,
  AlertTriangle, TrendingDown, TrendingUp, Eye, RefreshCw
} from 'lucide-react';
import { createShadowLedger, calculateEstimatedBalance } from '../../data/models/ShadowLedger';

const STORAGE_KEY = 'medfast_shadow_ledgers';

const ShadowLedgerManagement = () => {
  const [ledgers, setLedgers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    participantId: '', budgetId: '', apiRemainingBalance: 0,
    fundingAtRiskThreshold: 1000
  });

  const load = useCallback(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setLedgers(data);
    } catch { setLedgers([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveLedgers = (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setLedgers(data);
  };

  const filtered = ledgers.filter(l =>
    `${l.participantId} ${l.budgetId}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreate = () => {
    setEditingId(null);
    setFormData({ participantId: '', budgetId: '', apiRemainingBalance: 0, fundingAtRiskThreshold: 1000 });
    setShowForm(true);
    setError(null);
  };

  const openEdit = (ledger) => {
    setEditingId(ledger.id);
    setFormData({
      participantId: ledger.participantId || '',
      budgetId: ledger.budgetId || '',
      apiRemainingBalance: ledger.apiRemainingBalance || 0,
      fundingAtRiskThreshold: ledger.fundingAtRiskThreshold || 1000
    });
    setShowForm(true);
    setError(null);
  };

  const handleSave = () => {
    if (!formData.participantId.trim()) {
      setError('Participant ID is required');
      return;
    }

    const now = new Date().toISOString();
    if (editingId) {
      const updated = ledgers.map(l => {
        if (l.id !== editingId) return l;
        const merged = { ...l, ...formData, updatedAt: now, lastSyncedAt: now };
        const calc = calculateEstimatedBalance(merged);
        return { ...merged, ...calc };
      });
      saveLedgers(updated);
    } else {
      const newLedger = createShadowLedger({
        ...formData,
        id: `SL-${Date.now()}`,
        lastSyncedAt: now
      });
      const calc = calculateEstimatedBalance(newLedger);
      saveLedgers([...ledgers, { ...newLedger, ...calc }]);
    }
    setShowForm(false);
    setError(null);
  };

  const handleDelete = (id) => {
    saveLedgers(ledgers.filter(l => l.id !== id));
    setDeleteConfirm(null);
  };

  const recalcAll = () => {
    const updated = ledgers.map(l => {
      const calc = calculateEstimatedBalance(l);
      return { ...l, ...calc, updatedAt: new Date().toISOString() };
    });
    saveLedgers(updated);
  };

  const viewing = viewingId ? ledgers.find(l => l.id === viewingId) : null;
  const totalAtRisk = ledgers.filter(l => l.fundingAtRisk).length;
  const totalBalance = ledgers.reduce((s, l) => s + (l.estimatedBalance || l.apiRemainingBalance || 0), 0);

  const fmt = (v) => `$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Shadow Ledger (NDIS Budget Tracking)</h2>
          <p className="text-gray-600 text-sm">Track planned and delivered services to estimate remaining NDIS budgets</p>
        </div>
        <div className="flex gap-2">
          <button onClick={recalcAll} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm">
            <RefreshCw className="w-4 h-4" /> Recalculate
          </button>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
            <Plus className="w-4 h-4" /> Add Ledger
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg"><DollarSign className="w-5 h-5 text-indigo-600" /></div>
            <div>
              <p className="text-sm text-gray-500">Total Estimated Balance</p>
              <p className="text-lg font-bold text-gray-800">{fmt(totalBalance)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><TrendingUp className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-sm text-gray-500">Active Ledgers</p>
              <p className="text-lg font-bold text-gray-800">{ledgers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${totalAtRisk > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              {totalAtRisk > 0 ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <TrendingUp className="w-5 h-5 text-green-600" />}
            </div>
            <div>
              <p className="text-sm text-gray-500">Funding at Risk</p>
              <p className={`text-lg font-bold ${totalAtRisk > 0 ? 'text-red-600' : 'text-green-600'}`}>{totalAtRisk}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Search by participant or budget ID..." value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No shadow ledgers found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(l => {
            const balance = l.estimatedBalance ?? l.apiRemainingBalance ?? 0;
            return (
              <div key={l.id} className={`bg-white border rounded-xl p-5 hover:shadow-md transition-shadow ${l.fundingAtRisk ? 'border-red-300' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl ${l.fundingAtRisk ? 'bg-red-100' : 'bg-indigo-100'}`}>
                      {l.fundingAtRisk ? <TrendingDown className="w-6 h-6 text-red-600" /> : <DollarSign className="w-6 h-6 text-indigo-600" />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">Participant: {l.participantId || '—'}</h3>
                      <p className="text-sm text-gray-600">Budget: {l.budgetId || '—'}</p>
                      <div className="flex gap-4 mt-1 text-xs text-gray-500">
                        <span>API Balance: {fmt(l.apiRemainingBalance)}</span>
                        <span>Unbilled: {fmt(l.estimatedUnbilledAmount)}</span>
                        <span>Rostered: {fmt(l.estimatedRosteredAmount)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-lg font-bold ${balance < 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(balance)}</p>
                      <p className="text-xs text-gray-500">Est. Balance</p>
                    </div>
                    {l.fundingAtRisk && <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-medium">At Risk</span>}
                    <button onClick={() => setViewingId(l.id)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Eye className="w-4 h-4 text-gray-600" /></button>
                    <button onClick={() => openEdit(l)} className="p-1.5 hover:bg-indigo-100 rounded-lg"><Edit3 className="w-4 h-4 text-indigo-600" /></button>
                    <button onClick={() => setDeleteConfirm(l.id)} className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail View */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b bg-indigo-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-indigo-800">Ledger — {viewing.participantId}</h3>
              <button onClick={() => setViewingId(null)} className="p-1 hover:bg-indigo-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-medium text-gray-500">Budget ID</span><p>{viewing.budgetId || '—'}</p></div>
                <div><span className="font-medium text-gray-500">API Balance</span><p className="font-semibold">{fmt(viewing.apiRemainingBalance)}</p></div>
                <div><span className="font-medium text-gray-500">Estimated Balance</span><p className={`font-semibold ${(viewing.estimatedBalance || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>{fmt(viewing.estimatedBalance)}</p></div>
                <div><span className="font-medium text-gray-500">Risk Threshold</span><p>{fmt(viewing.fundingAtRiskThreshold)}</p></div>
                <div><span className="font-medium text-gray-500">Unbilled Services</span><p>{(viewing.unbilledDeliveredServices || []).length} ({fmt(viewing.estimatedUnbilledAmount)})</p></div>
                <div><span className="font-medium text-gray-500">Rostered Services</span><p>{(viewing.rosteredFutureServices || []).length} ({fmt(viewing.estimatedRosteredAmount)})</p></div>
                <div><span className="font-medium text-gray-500">Last Synced</span><p>{viewing.lastSyncedAt ? new Date(viewing.lastSyncedAt).toLocaleString() : '—'}</p></div>
                <div><span className="font-medium text-gray-500">Funding at Risk</span><p>{viewing.fundingAtRisk ? <span className="text-red-600 font-semibold">Yes</span> : <span className="text-green-600">No</span>}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b bg-indigo-600 text-white flex justify-between items-center rounded-t-2xl">
              <h2 className="text-xl font-bold">{editingId ? 'Edit Ledger' : 'Add Shadow Ledger'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Participant ID *</label>
                <input type="text" value={formData.participantId} onChange={e => setFormData(p => ({ ...p, participantId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Budget ID</label>
                <input type="text" value={formData.budgetId} onChange={e => setFormData(p => ({ ...p, budgetId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Remaining Balance ($)</label>
                <input type="number" step="0.01" value={formData.apiRemainingBalance}
                  onChange={e => setFormData(p => ({ ...p, apiRemainingBalance: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Funding at Risk Threshold ($)</label>
                <input type="number" step="0.01" value={formData.fundingAtRiskThreshold}
                  onChange={e => setFormData(p => ({ ...p, fundingAtRiskThreshold: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 font-medium"><Save className="w-4 h-4" />{editingId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-red-800 mb-2">Delete Shadow Ledger</h3>
            <p className="text-gray-600 mb-4">Remove this ledger for participant <strong>{ledgers.find(l => l.id === deleteConfirm)?.participantId}</strong>?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShadowLedgerManagement;
